import {
  mapEventProjectManagersToFormValues,
  unwrapPortalEventEnvelope,
} from "./eventApi";

/** Pull `bucket[key]` from flexible company-portal `/lookups` payloads. */
export const extractPortalLookupArray = (res, snakeKey) => {
  const root = res?.data?.data;
  if (Array.isArray(root)) {
    for (const bucket of root) {
      if (
        bucket &&
        typeof bucket === "object" &&
        Array.isArray(bucket[snakeKey])
      ) {
        return bucket[snakeKey];
      }
    }
  }
  if (root && typeof root === "object" && !Array.isArray(root)) {
    if (Array.isArray(root[snakeKey])) return root[snakeKey];
  }
  return [];
};

export const normalizePortalProjectManagerFilterOptions = (rows) => {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row) => {
      const admin = row.admin || row.admin_user || {};
      const adminId = admin.id ?? row.admin_id ?? row.id;
      const name = String(admin.name || admin.full_name || row.name || "").trim();
      const id =
        adminId !== undefined && adminId !== null && adminId !== ""
          ? adminId
          : row.id;
      return { id, name: name || String(id ?? "") };
    })
    .filter((o) => o.id !== undefined && o.id !== null && o.id !== "");
};

/**
 * List-row project managers from portal event attrs. Company Portal returns the
 * assignments under `company_user_events` (join rows with a nested `company_user`),
 * falling back to legacy `project_managers` — the same source `mapEventProjectManagersToFormValues`
 * reads. Shape the result to `{ admin: { name, image_url } }` for the shared events columns.
 */
const normalizeProjectManagersForRow = (attrs) =>
  mapEventProjectManagersToFormValues(attrs || {}).map((pm) => ({
    id: pm.company_user_id,
    admin: {
      id: pm.company_user_id,
      name: pm.name,
      image_url: pm.profile_picture_url,
    },
  }));

/** Display key for list rows (`event_type` string or nested object from portal attrs; prefers slug). */
const readEventCategoryKey = (source) => {
  if (!source || typeof source !== "object") return "";
  const raw = source.event_type;
  if (raw == null || raw === "") return "";
  if (typeof raw === "object") {
    const key =
      raw.slug ??
      raw.key ??
      raw.name_en ??
      raw.name ??
      raw.id;
    return String(key ?? "").trim().toLowerCase();
  }
  return String(raw).trim().toLowerCase();
};

/** Display label + id for list rows and filters (lookup-backed event types). */
const normalizeEventTypeFields = (attrs = {}) => {
  const raw = attrs.event_type ?? attrs.eventType;
  let event_type_id =
    attrs.event_type_id ?? attrs.eventTypeId ?? null;
  if (raw != null && typeof raw === "object") {
    if (event_type_id == null || event_type_id === "")
      event_type_id = raw.id ?? null;
    const label = String(
      raw.name ?? raw.name_en ?? raw.name_ar ?? raw.label ?? "",
    ).trim();
    const slug = String(
      raw.slug ?? raw.key ?? label,
    )
      .trim()
      .toLowerCase();
    return {
      event_type_id: event_type_id ?? null,
      event_type_label: label || slug,
      event_type_key: slug || readEventCategoryKey(attrs),
    };
  }
  const slug =
    raw != null && raw !== ""
      ? String(raw).trim().toLowerCase()
      : readEventCategoryKey(attrs);
  return {
    event_type_id: event_type_id ?? null,
    event_type_label: slug,
    event_type_key: slug,
  };
};

export const mapEventTypeLookupRowsToFilterOptions = (rows) => {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row) => ({
      id: row.id,
      name:
        String(row.name ?? row.name_en ?? row.name_ar ?? row.label ?? "").trim() ||
        String(row.id ?? ""),
    }))
    .filter((o) => o.id !== undefined && o.id !== null && o.id !== "");
};

export const readPortalEventsListMeta = (response) => {
  const headers = response?.headers || {};

  const fromHeaders = (...keys) => {
    for (const k of keys) {
      const raw = headers[k];
      if (raw != null && raw !== "") {
        const num = Number(raw);
        return Number.isFinite(num) ? num : NaN;
      }
    }
    return NaN;
  };

  let currentPage = fromHeaders("current-page", "Current-Page");
  let totalPages = fromHeaders("total-pages", "Total-Pages");
  let totalCount = fromHeaders("total-count", "Total-Count");

  const meta = response?.data?.meta ?? {};
  if (!Number.isFinite(currentPage)) {
    currentPage = Number(meta.current_page ?? meta.page) || 1;
  }
  if (!Number.isFinite(totalPages)) {
    totalPages = Number(meta.total_pages ?? meta.pages ?? 1) || 1;
  }
  if (!Number.isFinite(totalCount)) {
    totalCount = Number(meta.total_count ?? meta.count ?? 0) || 0;
  }

  return {
    currentPage: currentPage > 0 ? currentPage : 1,
    pages: totalPages > 0 ? totalPages : 1,
    count: totalCount >= 0 ? totalCount : 0,
  };
};

/**
 * Flat rows for portal GET /events: supports JSON:API `{ data: Resource[] }` or plain `{ data: [] }`.
 */
export const flattenCompanyPortalEventsList = (response) => {
  const payload = response?.data;
  const nodes = payload?.data;
  const items = Array.isArray(nodes)
    ? nodes
    : nodes != null && typeof nodes === "object"
      ? [nodes]
      : [];

  /** @type {Array<ReturnType<typeof mapPortalEventListRow>>} */
  const rows = [];

  for (const node of items) {
    const envelope = unwrapPortalEventEnvelope({
      data: { data: node, included: payload?.included },
    });
    let row = envelope ? mapPortalEventListRow(envelope) : null;
    if (!row) row = mapPlainPortalEventRow(node);
    if (row) rows.push(row);
  }

  return rows;
};

const mapPortalEventListRow = (envelopeOrNull) => {
  if (!envelopeOrNull) return null;

  let id;
  /** @type {Record<string, any>} */
  let attrs;

  if (
    envelopeOrNull.attributes != null &&
    typeof envelopeOrNull.attributes === "object"
  ) {
    id = envelopeOrNull.id ?? envelopeOrNull.attributes?.id;
    attrs = envelopeOrNull.attributes;
  } else if (typeof envelopeOrNull === "object") {
    attrs = envelopeOrNull;
    id = attrs.id;
  }

  if (id === undefined || id === null) return null;

  const et = normalizeEventTypeFields(attrs);

  return {
    id: String(id),
    name: attrs.name ?? "",
    event_type: et.event_type_key,
    event_type_id: et.event_type_id,
    event_type_label: et.event_type_label,
    payment_period: attrs.payment_period ?? attrs.paymentPeriod,
    event_start_at: attrs.event_start_at ?? attrs.starts_at,
    event_end_at: attrs.event_end_at ?? attrs.ends_at,
    status: attrs.status,
    project_managers: normalizeProjectManagersForRow(attrs),
  };
};

const mapPlainPortalEventRow = (row) => {
  if (!row || typeof row !== "object") return null;

  const base =
    row.attributes != null &&
    typeof row.attributes === "object" &&
    !Array.isArray(row.attributes)
      ? { ...row.attributes, id: row.id ?? row.attributes.id }
      : row;

  const id = base.id ?? base.identifier;
  if (id === undefined || id === null) return null;

  const et = normalizeEventTypeFields(base);

  return {
    id: String(id),
    name: base.name ?? "",
    event_type: et.event_type_key,
    event_type_id: et.event_type_id,
    event_type_label: et.event_type_label,
    payment_period: base.payment_period ?? base.paymentPeriod,
    event_start_at: base.event_start_at ?? base.starts_at,
    event_end_at: base.event_end_at ?? base.ends_at,
    status: base.status,
    project_managers: normalizeProjectManagersForRow(base),
  };
};
